import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../lib/api';
import { getHostInfo } from '../lib/hosts';
import ThemeToggle from '../components/ThemeToggle';
import { toast } from 'sonner';
import BrandedToaster from '../components/BrandedToaster';
import SsoButtons from '../components/SsoButtons';

// The password rule lives in lib/passwordRule.js (one number for every screen, 14/09/2026);
// re-exported here because the welcome screen and older imports read it from this page.
import { MIN_PASSWORD_LENGTH } from '../lib/passwordRule';
import { MONTHLY } from '../lib/pricing';
export { MIN_PASSWORD_LENGTH };

// Where the scoped sign-up token lives between /register and /welcome. Exported so the
// welcome screen reads the same key rather than a second copy of the string.
export const SIGNUP_TOKEN_KEY = 'hh_signup_token';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // TWO ANSWERS. The company name and the person's name are asked on /welcome, which
  // cannot be got past — see SIGNUP_TOKEN_KEY below and routers/onboarding.py.
  const [formData, setFormData] = useState({
    admin_email: '',
    admin_password: '',
    country_code: 'GB',
    // The honeypot (Group A7) — see the hidden box below. Always '' for a person.
    website: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await register(formData);
      if (res?.signup_token) {
        // ⚠️ sessionStorage, under its OWN key — never localStorage `token`. This is a
        // scoped token good only for /welcome. Stored as `token` it would make the app
        // think somebody is signed in, and would be sent to every endpoint in the API,
        // all of which now refuse it (auth._refuse_scoped_token). sessionStorage also
        // means closing the tab drops it, which is the right lifetime for a half-finished
        // sign-up.
        sessionStorage.setItem(SIGNUP_TOKEN_KEY, res.signup_token);
        navigate('/welcome', {
          replace: true,
          state: { email: res.email || formData.admin_email },
        });
      } else if (res?.verification_required) {
        // Hard gate: no auto-login. Send them to the confirm-your-email PAGE.
        //
        // ⚠️ NAVIGATE — DO NOT SWAP THIS PAGE'S OWN DOM. That is what it used to do, and
        // it cost two separate things:
        //   1. Password managers offer to save a login when a submission is followed by a
        //      navigation. There wasn't one, so LastPass stayed silent at the moment the
        //      account was created and only woke up later (Rob, 15/08/2026).
        //   2. The confirmation screen wasn't a URL, so refreshing it destroyed it with no
        //      way back — and signing up again just said "Email already registered".
        // ⚠️ `replace: true` so Back goes to wherever they came from, not to a sign-up
        // form for an account that now exists.
        navigate('/verify-email', {
          replace: true,
          state: { sentTo: res.email || formData.admin_email },
        });
      } else {
        // Fallback (shouldn't happen with the current backend).
        navigate('/onboarding');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // The trial length is superadmin-tunable, so this page must ASK rather than
  // assume. ⚠️ null until the answer arrives, and the bullet is not rendered
  // until then — a hardcoded "14" here would tell a 30-day customer something
  // untrue on every single page load, briefly, and nobody would ever see it
  // happen. A moment's pop-in beats a moment's lie.
  const [trialDays, setTrialDays] = useState(null);

  useEffect(() => {
    let cancelled = false;
    authApi.signupInfo()
      .then((r) => {
        const n = parseInt(r?.data?.trial_period_days, 10);
        if (!cancelled && Number.isFinite(n) && n > 0) setTrialDays(n);
      })
      .catch(() => {});   // offer still reads fine without the number
    return () => { cancelled = true; };
  }, []);

  const benefits = [
    ...(trialDays ? [`${trialDays}-day free trial`] : []),
    "No credit card required",
    "All features included",
    "Cancel anytime"
  ];

  return (
    <div className="min-h-screen bg-background flex">
      <BrandedToaster />
      <ThemeToggle iconOnly variant="ghost" className="fixed top-4 right-4 z-50 text-muted-foreground hover:text-foreground" />

      
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          {/* Same as LoginPage: the logo goes HOME (marketing site), and "/" on
              the app surface is not home. Plain <a> — different origin. */}
          <a href={getHostInfo().marketingOrigin} className="inline-flex mb-8" data-testid="logo">
            <Logo size={32} />
          </a>

          {/* ⚠️ The "Confirm your email" panel used to live here, shown by swapping this
              page's own DOM. It is now a real page at /verify-email — see handleSubmit. */}
          <h1 className="font-heading text-3xl font-bold mb-2">Start your free trial</h1>
          <p className="text-muted-foreground mb-8">
            Set up your team's leave management in minutes
          </p>

          {/* ⚠️ TWO BOXES. The company name and the person's name are asked on /welcome,
              straight after this, on a screen that cannot be skipped. They were moved, not
              dropped — see routers/onboarding.py::update_essentials. Adding a field back
              here is not a small change: five boxes is what stopped password managers
              recognising this as a sign-up form at all. */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="admin_email">Work email</Label>
              <Input
                id="admin_email"
                name="username"
                type="email"
                autoComplete="username"
                value={formData.admin_email}
                onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                className="input-hive"
                required
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin_password">Password</Label>
              <div className="relative">
                <Input
                  id="admin_password"
                  name="new-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={formData.admin_password}
                  onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
                  className="input-hive pr-10"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {/* ⚠️ SAID BEFORE THEY FAIL, NOT AFTER. This used to be enforced only by the
                  browser's own grey bubble, which nobody sees until they have already
                  typed something and been rejected. The real rule lives on the server
                  (auth.MIN_PASSWORD_LENGTH) — this is the courtesy of saying so. */}
              <p className="text-sm text-muted-foreground">
                At least {MIN_PASSWORD_LENGTH} characters
              </p>
            </div>

            {/* ⚠️ THE HIDDEN BOX (Group A7, 21/10/2026). Off screen, out of the tab order and
                hidden from screen readers, so a person never fills it; a bot filling every
                field does, and the server quietly creates nothing (services/signup_guard.py).
                Same shape as the interest form's. autoComplete off so a browser never fills
                it on a person's behalf. */}
            <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="register-website">Website</label>
              <input id="register-website" name="website" type="text" tabIndex={-1} autoComplete="off"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
            </div>

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </Button>

            {/* ⚠️ THESE TWO DOCUMENTS DO NOT EXIST YET — see PAGES-NEEDED.md. The links go
                to honest holding pages rather than nowhere, and the privacy policy is a UK
                GDPR obligation, not a nicety: a customer handing over their staff's names,
                emails and sickness records is entitled to know what happens to them. */}
            <p className="text-center text-sm text-muted-foreground">
              By creating an account you're agreeing to our{' '}
              <Link to="/terms" className="text-primary hover:underline">terms of service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-primary hover:underline">privacy policy</Link>.
            </p>
          </form>

          {/* Below the form, with a divider — the shape Timetastic uses, and the one Rob
              picked. Draws nothing at all unless a provider is configured. */}
          <SsoButtons />

          <p className="text-center text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Branding */}
      <div className="hidden lg:flex flex-1 bg-card items-center justify-center relative overflow-hidden">
        <div className="hero-glow absolute inset-0" />
        <div className="hex-pattern absolute inset-0 opacity-20" />
        <div className="relative max-w-md px-8">
          <div className="card-hive p-8 backdrop-blur-sm bg-card/80">
            <h2 className="font-heading text-2xl font-bold mb-6">
              What you get with HolidayHive
            </h2>
            <ul className="space-y-4">
              {benefits.map((benefit) => (
                <li key={benefit} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-4 h-4 text-primary" />
                  </div>
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-border">
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-4xl font-bold text-primary">{MONTHLY}</span>
                <span className="text-muted-foreground">/user/month</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                All features included. No hidden costs.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
